import {
    fetchActiveTimerSession,
    fetchDesignerPickerItems,
    fetchSubTicketDetail,
    fetchSubTicketStatusLog,
    fetchSubTicketSummary,
    fetchSubTickets,
} from './effects';
import {
    setDesignerFilter,
    setDueDateRange,
    setExpandedSubTicketId,
    setIsArchivedShown,
    setIsOnlyMineShown,
    setStatusFilter,
} from './events';
import {
    ACTIVE_SESSION_KEY,
    designerPickerKey,
    detailKey,
    isCurrentRead,
    isLatestGeneration,
    isScopeOwner,
    listKey,
    summaryKey,
} from './requestGeneration';
import {
    $activeTimerSession,
    $activeTimerSessionError,
    $designerFilter,
    $designerPicker,
    $dueDateRange,
    $expandedSubTicketId,
    $isActiveTimerSessionLoading,
    $isArchivedShown,
    $isOnlyMineShown,
    $statusFilter,
    $subTicketDetailStates,
    $subTicketList,
    $subTicketStatusLog,
    $subTicketSummary,
} from './states';

// Every read below applies only while it is still the newest request of its key AND still for
// the Request its scope is showing. A reply that lost either race is dropped, so a slow response
// for a previous task, a previous filter, a different sub-ticket, or a mutation refresh issued
// before the user navigated away can never overwrite what the user is looking at.

// ------------------------------------------------------------------- sub-ticket list
// Starting a read for a DIFFERENT Request withholds the rows immediately. Waiting for the reply
// would leave the previous Request's phases on screen under this one's heading — and if that
// reply then failed, they would simply stay there.
$subTicketList.on(fetchSubTickets, (state, params) => {
    const taskId = params.filter.task_id;

    // A read the page is no longer showing may not put it back into loading
    if (!isScopeOwner('page', taskId)) {
        return state;
    }

    return {
        status: 'loading',
        taskId,
        collection: state.taskId === taskId ? state.collection : null,
    };
});

$subTicketList.on(fetchSubTickets.done, (state, { params, result }) => {
    const taskId = params.filter.task_id;

    return isCurrentRead('page', listKey(taskId), taskId, params.generation)
        ? { status: 'loaded', taskId, collection: result }
        : state;
});

// A failed read IS settled: it will not answer on its own, and a consumer waiting for it to
// become a list would wait forever.
//
// Whatever the store holds here already belongs to this Request — the invocation reducer above
// withheld anything else the moment this read started — so a failure leaves it alone rather than
// resurrecting or discarding rows. A failed REFRESH of a loaded Request keeps what it had; a
// failed FIRST read has nothing to keep.
$subTicketList.on(fetchSubTickets.fail, (state, { params }) => {
    const taskId = params.filter.task_id;

    return isCurrentRead('page', listKey(taskId), taskId, params.generation)
        ? { status: 'error', taskId, collection: state.collection }
        : state;
});

// ------------------------------------------------------------------- aside summary
// Aside-scoped: it survives the Sub Tasks page unmounting, but not a change of Request
$subTicketSummary.on(fetchSubTicketSummary.done, (state, { params, result }) => {
    const taskId = params.filter.task_id;

    return isCurrentRead('aside', summaryKey(taskId), taskId, params.generation) ? result : state;
});

// ------------------------------------------------------------------- designer picker
// Same rule as the list: a read for another Request withholds the current items at once, so a
// modal can never be seeded from, or submit, a designer belonging to a different Request.
$designerPicker.on(fetchDesignerPickerItems, (state, params) => {
    const taskId = params.filter.task_id;

    if (!isScopeOwner('page', taskId)) {
        return state;
    }

    return { status: 'loading', taskId, items: state.taskId === taskId ? state.items : null };
});

$designerPicker.on(fetchDesignerPickerItems.done, (state, { params, result }) => {
    const taskId = params.filter.task_id;

    return isCurrentRead('page', designerPickerKey(taskId), taskId, params.generation)
        ? { status: 'loaded', taskId, items: result.items }
        : state;
});

// As with the list, anything still held here belongs to this Request — the invocation reducer
// withheld anything else — so a failed refresh keeps this Request's designers and a failed first
// read has none to keep
$designerPicker.on(fetchDesignerPickerItems.fail, (state, { params }) => {
    const taskId = params.filter.task_id;

    return isCurrentRead('page', designerPickerKey(taskId), taskId, params.generation)
        ? { status: 'error', taskId, items: state.items }
        : state;
});

// ---------------------------------------------------------------------- detail, keyed by id
// Marking loading on invocation is what stops a second panel showing a stale neighbour's data
$subTicketDetailStates.on(fetchSubTicketDetail, (state, params) => ({
    ...state,
    [params.id]: { status: 'loading', detail: state[params.id]?.detail ?? null },
}));

$subTicketDetailStates.on(fetchSubTicketDetail.done, (state, { params, result }) =>
    isLatestGeneration(detailKey(params.taskId, params.id), params.generation)
        ? { ...state, [params.id]: { status: 'loaded', detail: result } }
        : state
);

// A failure is recorded against that id alone; every other panel is untouched
$subTicketDetailStates.on(fetchSubTicketDetail.fail, (state, { params }) =>
    isLatestGeneration(detailKey(params.taskId, params.id), params.generation)
        ? { ...state, [params.id]: { status: 'error', detail: state[params.id]?.detail ?? null } }
        : state
);

// -------------------------------------------------------------------------- active session
// The open session is global to the designer rather than to a Request, so it is guarded by
// generation alone — there is no owning task for it to belong to.
//
// Every newest invocation puts the UI back into "restoring" immediately — including the 409
// recovery read — so Start cannot be re-enabled in the gap before the answer arrives.
$isActiveTimerSessionLoading.on(fetchActiveTimerSession, () => true);
$activeTimerSessionError.on(fetchActiveTimerSession, () => false);

$activeTimerSession.on(fetchActiveTimerSession.done, (state, { params, result }) =>
    isLatestGeneration(ACTIVE_SESSION_KEY, params.generation) ? result : state
);

// A failed read is not a confirmed absence: the session store is left alone and the error is
// flagged, so the UI says "could not check" rather than offering a Start that would 409 again
$activeTimerSessionError.on(fetchActiveTimerSession.fail, (state, { params }) =>
    isLatestGeneration(ACTIVE_SESSION_KEY, params.generation) ? true : state
);

// Only the newest generation may end restoration
$isActiveTimerSessionLoading.on(fetchActiveTimerSession.finally, (state, payload) =>
    isLatestGeneration(ACTIVE_SESSION_KEY, payload.params.generation) ? false : state
);

// Filters
$isArchivedShown.on(setIsArchivedShown, (_, data) => data);
$isOnlyMineShown.on(setIsOnlyMineShown, (_, data) => data);
$statusFilter.on(setStatusFilter, (_, data) => data);
$designerFilter.on(setDesignerFilter, (_, data) => data);
$dueDateRange.on(setDueDateRange, (_, data) => data);
$expandedSubTicketId.on(setExpandedSubTicketId, (_, data) => data);

// ------------------------------------------------------------------- sub-task status log
// Stamped with the Request it was read for, so a reply that arrives after the user moved on is
// simply never rendered rather than merged into the timeline now on screen.
$subTicketStatusLog.on(fetchSubTicketStatusLog.done, (_, { params, result }) => ({
    taskId: params.filter.task_id,
    items: result.items,
}));
